import { useApolloClient, useLazyQuery } from '@apollo/react-hooks';
import Box from '@material-ui/core/Box';
import React, { useEffect } from 'react';
import CustomAutoComplete from '../../../../common/components/CustomAutoComplete';
import CustomTextField from '../../../../common/components/CustomTextField';
import { Role } from '../../../../common/constants';
import { displaySnackBar } from '../../../../common/displaySnackbar';
import { GET_COLLABORATORS } from '../../../../graphql/user/query';
import ConfirmationButtons from '../../../ConfirmationButtons/ConfirmationButtons';
import { ICommission } from '../utils';
import {
  Collaborators,
  CollaboratorsVariables,
} from '../../../../graphql/user/types/Collaborators';
import { orderBy } from 'lodash';
import CustomDialog from '../../../../common/components/CustomDialog';

interface IEditUserCommissionModal {
  isEditingCommission: boolean;
  role?: Role[];
  commissionInfo?: ICommission;
  totalCommissionMax?: number;
  onConfirmEditingCommission: (commissionInfo: ICommission) => void;
  onCancelEdit: () => void;
  currentBeneficiaryIds: number[];
  userId?: number;
  onClose?: () => void;
}

type UserAutoCompleteType = {
  label: string;
  value: any;
  echelon: string;
  priority: number;
};

export const EditUserCommissionModal: React.FC<IEditUserCommissionModal> = (
  props,
) => {
  const {
    isEditingCommission,
    role,
    commissionInfo,
    onConfirmEditingCommission,
    onCancelEdit,
    totalCommissionMax,
    currentBeneficiaryIds,
    userId,
    onClose,
  } = props;

  const [search, setSearch] = React.useState<string>('');
  const [_commisionInfo, setCommisionInfo] = React.useState<ICommission>({
    id: 0,
    beneficiary: { id: 0, firstName: '', lastName: '' },
    amount: 0,
    isNew: true,
  });

  const [getAllUsers, { data, loading }] = useGetAllUsers(
    currentBeneficiaryIds,
    userId,
  );

  const handleCancel = () => {
    onCancelEdit();
  };

  const handleAutoCompleteChange = (value: string) => {
    setSearch(value);
  };

  const handleConfirm = () => {
    onConfirmEditingCommission(_commisionInfo);
  };

  const generateGroupedLabelEchelon = (echelon: string) => {
    switch (echelon) {
      case 'AGENCE':
        return 'COLLABORATEURS AGENCE';
      case 'GROUPEMENT':
        return 'AUTRES COLLABORATEURS GROUPEMENT';
      default:
        return 'AUTRES COLLABORATEURS ENSEIGNE';
    }
  };

  const autoCompleteUsersOptions: UserAutoCompleteType[] = React.useMemo(() => {
    const currentOptions =
      data?.collaborators?.map((user) => ({
        label: `${user?.firstName} ${user?.lastName}`,
        value: user?.id,
        echelon: user?.entities?.length
          ? generateGroupedLabelEchelon(
              user?.entities[0]?.echelon?.name as string,
            )
          : '',
        priority: user?.entities?.length
          ? (user?.entities[0]?.echelon?.priority as number)
          : 0,
      })) || [];

    if (commissionInfo) {
      const currentBeneficiary = data?.collaborators?.find(
        (collaborator) => collaborator?.id === commissionInfo?.id,
      );
      if (currentBeneficiary)
        currentOptions.push({
          label:
            (currentBeneficiary?.firstName || '') +
              ' ' +
              currentBeneficiary?.lastName || '',
          value: currentBeneficiary?.id || 0,
          echelon: currentBeneficiary?.entities?.length
            ? generateGroupedLabelEchelon(
                currentBeneficiary?.entities[0]?.echelon?.name as string,
              )
            : '',
          priority: currentBeneficiary?.entities?.length
            ? (currentBeneficiary?.entities[0]?.echelon?.priority as number)
            : 0,
        });
    }
    
    return currentOptions;
  }, [data, commissionInfo]);

  const users = data?.collaborators || [];

  const defaultUser = autoCompleteUsersOptions?.reduce(
    (acc, current) =>
      commissionInfo?.beneficiary?.id &&
      current.value === commissionInfo?.beneficiary?.id
        ? current
        : acc,
    {} as any,
  );

  useEffect(() => {
    if (userId)
      getAllUsers({
        variables: {
          input: {
            role,
            userIdNotIn: currentBeneficiaryIds.filter(
              (id) => id !== commissionInfo?.beneficiary?.id,
            ),
            userId,
          },
        },
      });
  }, [
    commissionInfo,
    currentBeneficiaryIds,
    getAllUsers,
    isEditingCommission,
    role,
    userId,
  ]);

  useEffect(() => {
    if (commissionInfo) {
      setCommisionInfo(commissionInfo);
    }
  }, [commissionInfo, autoCompleteUsersOptions]);

  const beneficiaryOptions = orderBy(autoCompleteUsersOptions, 'priority', [
    'desc',
  ]);

  return (
    <CustomDialog
      loading={false}
      maxWidth="sm"
      onClose={onClose}
      open={isEditingCommission}
      actions={
        <ConfirmationButtons
          handleCancel={handleCancel}
          handleConfirm={handleConfirm}
          disableConfirm={
            !_commisionInfo?.beneficiary?.id ||
            (totalCommissionMax && _commisionInfo?.amount
              ? _commisionInfo?.amount >= totalCommissionMax
              : false) ||
            !_commisionInfo?.amount
          }
          confirmText="Confirmer"
        />
      }>
      <CustomAutoComplete<UserAutoCompleteType>
        inputValue={search}
        label="Bénéficiaire"
        loading={loading}
        optionValue={commissionInfo ? defaultUser : undefined}
        onChange={handleAutoCompleteChange}
        options={beneficiaryOptions}
        groupBy={(option) => option.echelon}
        onAutoCompleteChange={(option) => {
          setCommisionInfo((prevState) => {
            const currentUser = users?.find(
              (user) => user?.id === option?.value,
            );
            return {
              ...prevState,
              beneficiary: {
                id: option?.value,
                lastName: currentUser?.lastName || '',
                firstName: currentUser?.firstName || '',
              },
            };
          });
        }}
      />

      <Box height={16} />

      <CustomTextField
        type="number"
        value={_commisionInfo?.amount || ''}
        label="Montant"
        onChange={(event) => {
          const amount = event?.target?.value || '0';
          setCommisionInfo((prevState) => ({
            ...prevState,
            amount: Number.parseInt(amount),
          }));
        }}
      />
    </CustomDialog>
  );
};

const useGetAllUsers = (userIdNotIn: number[], userId?: number) => {
  const client = useApolloClient();
  return useLazyQuery<Collaborators, CollaboratorsVariables>(
    GET_COLLABORATORS,
    {
      fetchPolicy: 'network-only',
      variables: {
        input: { userIdNotIn, userId, onlyManager: false },
      },
      onError: (error) => {
        if (error && error.graphQLErrors && error.graphQLErrors.length) {
          displaySnackBar(client, {
            isOpen: true,
            message: 'Une erreur est survenue.',
            type: 'ERROR',
          });
        }
      },
    },
  );
};
